/**
 * AI プロンプト組立ロジック (buildPrompt 等) の **正本**。
 *
 * 元は functions/src/ai/promptBuilder.ts にあったが、admin の統合エディタ「ライブプレビュー」が
 * 「実際に AI へ送るデータそのもの」を表示できるよう、純粋ロジックのみ shared-types へ移設した
 * (方式A、2026-06-02)。functions (analyzePost) と admin (preview) が **同一コード**を使うことで、
 * プレビューがバックエンドと完全一致し、将来もドリフトしない。
 *
 * 本モジュールは純粋関数のみ (firebase-admin / functions 依存なし)。実際の Gemini 呼出・画像取得・
 * API キーはバックエンド (functions/src/ai/analyzePost.ts) 側に残る。
 *
 * 移設時の不変性: 関数本体は verbatim コピー。import ヘッダのみ差し替え (旧 `./types.js` 別名を
 * canonical 名 + local alias に置換)。プロンプト出力は不変 (既存 promptBuilder テストで検証)。
 */
import { FIELD_TYPE_LABELS, findOtherOption, isOtherChosen, normalizeAiOutputFormat, normalizeFieldOptions, readOtherText, } from '../domain/index.js';
const DISALLOWED_MD = /`{3,}[\s\S]*?`{3,}/gm;
/**
 * 厳格出力ルール (old2 promptBuilder `DEFAULT_STRICT_RULES` 踏襲、docs/105 Phase 3)。
 * promptSections モード時に「ルール」セクションへ常時注入される。
 */
const DEFAULT_STRICT_RULES = `【厳格出力ルール】
- 出力は **純粋な JSON オブジェクト1個のみ**。先頭/末尾に空白以外の文字（説明・コードフェンス・見出し・注釈）は一切出力しない。
- 最初の1文字は「{」、最後の1文字は「}」。
- 文字列中のダブルクォートは \\" にエスケープするか、日本語の全角カギ括弧「」を用いる。
- バックスラッシュは有効なエスケープのみ（\\" \\\\ \\n \\t）。改行は \\n。**末尾カンマ・JS スタイルコメント (// /*) は禁止**。
- 絵文字・制御文字・Markdown 記法は禁止。
- スキーマ外キーは禁止。
- 文字数の目安に合わせるために出力を途中で打ち切らないこと。**JSON は必ず完結した形で出力する**。指定文字数に満たなくても、内容を網羅していれば問題ない。`;
export function sanitizeForPrompt(input) {
    if (input === null || input === undefined)
        return '';
    let s = String(input);
    s = s.replace(DISALLOWED_MD, ' ');
    s = Array.from(s)
        .filter((ch) => {
        const c = ch.charCodeAt(0);
        return c >= 32 || ch === '\t' || ch === '\n';
    })
        .join('');
    s = s.replace(/"/g, '「').replace(/'/g, '’');
    return s.trim();
}
/**
 * 投稿データ (利用者の入力) をプロンプトに入れるときの長さの上限 (2026-10-22、docs/13 §7)。
 *
 * 投稿の項目はそのまま systemInstruction に入るため、極端に長い入力で Gemini の入力トークンを
 * 膨らませる・指示文を押し流すことを防ぐ。値は UI の既定の上限 (タイトル 100 字 / 本文 1000 字 /
 * 自由記述 1000 字、地区の設定で変えられる) より十分大きくし、通常の投稿では切られない
 * (= プロンプトが変わらない) ようにしている。切ったときだけ末尾に省略の印を付ける。
 */
export const PROMPT_POST_VALUE_LIMITS = {
    title: 200,
    comment: 4000,
    /** extraFields の各値 (選択肢「その他」の記入内容を含む)。 */
    extraField: 2000,
};
const TRUNCATED_MARK = '…（以下省略）';
/** 文字 (コードポイント) 数で切る。切ったときだけ省略の印を付ける。 */
export function truncateForPrompt(value, max) {
    const chars = Array.from(value);
    if (chars.length <= max)
        return value;
    return chars.slice(0, max).join('').trimEnd() + TRUNCATED_MARK;
}
/** promptInputMapping の source (`post.title` / `post.extraFields.x` / `a|b`) から上限を決める。 */
export function postValueLimitForSource(source) {
    const candidates = source
        .split('|')
        .map((c) => c.trim().replace(/^post\./, ''))
        .filter(Boolean);
    if (candidates.length > 0 && candidates.every((c) => c === 'title')) {
        return PROMPT_POST_VALUE_LIMITS.title;
    }
    if (candidates.some((c) => c === 'comment'))
        return PROMPT_POST_VALUE_LIMITS.comment;
    if (candidates.some((c) => c === 'title'))
        return PROMPT_POST_VALUE_LIMITS.title;
    return PROMPT_POST_VALUE_LIMITS.extraField;
}
/** 投稿データの値をプロンプト用に整える (sanitize + 長さの上限)。 */
function sanitizePostValue(raw, source) {
    return truncateForPrompt(sanitizeForPrompt(raw), postValueLimitForSource(source));
}
/**
 * docs/105 M2 連鎖ルールをプロンプト用テキストに展開する (old2 promptBuilder の chain 展開を踏襲)。
 * - chainGlobalRules: マスタ全体の制約 (forbiddenAsPrimary / forbiddenAsSub / note)。
 * - rows[].chainRules: 行単位の trigger → result 連鎖。
 * 未設定なら空配列を返し、formatCategoryMaster の出力は従来どおり (現行挙動保持)。
 */
function formatChainRuleSections(master) {
    const parts = [];
    // グローバル制約 (使用ルール直後のセクションとして展開)
    const g = master.chainGlobalRules;
    if (g && (g.forbiddenAsSub?.length || g.forbiddenAsPrimary?.length || g.note)) {
        const gLines = ['【連鎖ルール（グローバル制約）】'];
        if (g.forbiddenAsPrimary?.length) {
            gLines.push(`- 一次（主軸）に置けないコード: ${g.forbiddenAsPrimary.join(', ')}`);
        }
        if (g.forbiddenAsSub?.length) {
            gLines.push(`- 連鎖先（sub 等の連鎖列）に置けないコード: ${g.forbiddenAsSub.join(', ')}`);
        }
        if (g.note?.trim())
            gLines.push(`- ${g.note.trim()}`);
        parts.push(gLines.join('\n'));
    }
    // 行単位の連鎖ルール
    const rowsWithChain = (master.rows ?? []).filter((r) => r.chainRules && r.chainRules.length > 0);
    if (rowsWithChain.length > 0) {
        const chainLines = ['【連鎖ルール（行別）】'];
        chainLines.push('「対象がトリガーコードを伴う場合、結果コードを連鎖列に必ず含める」という指示。');
        for (const r of rowsWithChain) {
            for (const rule of r.chainRules ?? []) {
                const trig = rule.triggerCodes.join(', ');
                const res = rule.resultCodes.join(', ');
                const note = rule.note?.trim() ? `（${rule.note.trim()}）` : '';
                chainLines.push(`- ${r.majorCd ?? ''}-${r.categoryCd} (${r.category}) × {${trig}} → {${res}}${note}`);
            }
        }
        parts.push(chainLines.join('\n'));
    }
    return parts;
}
export function formatCategoryMaster(master) {
    if (!master || !master.rows?.length)
        return '（マスタ未設定）';
    const parts = [];
    // old2 踏襲 (promptBuilder.ts:42-94): マスタ定義 + 使用ルールを先頭に置く。これが無いと AI が
    // 許可値の正しい形式を把握できず誤形式を返す。例: 感情マスタの usageRules には
    // 「emotions に使うラベルは category 列の日本語名（喜び・信頼…）のみ」と bare 値が列挙されており、
    // これが欠落すると AI は行に見える「ポジティブ/喜び」形式をコピーして whitelist に全消しされる。
    if (master.definition?.trim())
        parts.push(`【マスタ定義】${master.definition.trim()}`);
    if (master.usageRules?.trim())
        parts.push(`【使用ルール】${master.usageRules.trim()}`);
    // docs/105 M2: 連鎖ルール (グローバル制約 + 行別) があれば分類行の前に展開する (old2 踏襲)。
    parts.push(...formatChainRuleSections(master));
    // old2 踏襲: 常に【分類項目】見出しを出し、各行に判断基準・定義を付す。
    parts.push('【分類項目】');
    for (const r of master.rows) {
        // v2 shared-types AiCategoryMasterRow の example / criteria / definition を unknown 経由で参照。
        const extra = r;
        const lines = [
            `- ${r.majorCd ?? ''}-${r.categoryCd}: ${r.major ?? ''}/${r.category}（${extra.example ?? ''}）`,
        ];
        if (extra.criteria?.trim())
            lines.push(`  【判断基準】${extra.criteria.trim()}`);
        if (extra.definition?.trim())
            lines.push(`  【定義】${extra.definition.trim()}`);
        parts.push(lines.join('\n'));
    }
    return parts.join('\n');
}
function resolveFromPath(root, path) {
    const parts = path.split('.');
    let cur = root;
    for (const p of parts) {
        if (cur && typeof cur === 'object' && p in cur) {
            cur = cur[p];
        }
        else {
            return undefined;
        }
    }
    return cur;
}
function resolveFromMapping(post, source) {
    for (const candidate of source
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean)) {
        // 参照元は `post.title` / `post.extraFields.x` が正。`post.` の無い旧形式 (エディタが以前に
        // 自動生成した `extraFields.x` / `title`) は投稿直下から引く (2026-10-08、以前は値が取れていなかった)。
        const v = resolveFromPath({ post }, candidate) ??
            (candidate.startsWith('post.') ? undefined : resolveFromPath(post, candidate));
        if (v !== undefined && v !== null && v !== '')
            return v;
    }
    return '';
}
function normalizeScore(v) {
    const n = Number(v);
    if (!Number.isFinite(n))
        return null;
    const i = Math.trunc(n);
    return [-3, -2, -1, 1, 2, 3].includes(i) ? i : null;
}
// ============================================================================
// 列名動的化レベル 3 (docs/105、old2 promptBuilder fields[] レンダリング移植)。
// AiOutputFormatSchema.fields[] が設定されたフォーマットは、以下の helper で
// 出力スキーマ・集約ルールを完全にデータ駆動で生成する (mode 非依存)。
// fields[] 未設定の既存フォーマットは従来の legacy renderer (formatOutputSchema 後半) を
// そのまま使用するため、現行挙動は一切変わらない。
// ============================================================================
function lengthHint(min, max) {
    if (min && max)
        return `日本語${min}〜${max}文字程度（目安）`;
    if (max)
        return `日本語${max}文字程度まで（目安）`;
    if (min)
        return `日本語${min}文字程度以上（目安）`;
    return '日本語';
}
function describeValueSource(source, prefix) {
    // source 欠落データ (手編集 JSON 等) でもプロンプト生成を止めない (normalizeAiOutputFormat で
    // 補完済みのはずだが二重防御)。
    if (source?.type === 'master') {
        const col = source.valueFrom === 'category'
            ? 'category 列の文字列'
            : source.valueFrom === 'major-category-code'
                ? '"大分類CD-カテゴリCD" 形式のコード'
                : source.valueFrom === 'major-code'
                    ? 'majorCd（大分類コード）'
                    : 'categoryCd（カテゴリコード）';
        return `${prefix}マスタ ${source.masterId} の ${col}`;
    }
    if (source?.type === 'inline' && source.values.length > 0) {
        return `${prefix}次のいずれか: ${source.values.join(' / ')}`;
    }
    return prefix.trim();
}
function buildTextSpec(f) {
    const lengthPart = f.lengthMin || f.lengthMax ? `（${lengthHint(f.lengthMin, f.lengthMax)}）` : '';
    const opt = f.optional ? '[任意] ' : '';
    return `${opt}${f.definition || f.label}${lengthPart}`;
}
function buildEnumArraySpec(f) {
    const maxPart = f.maxItems ? `最大${f.maxItems}件・` : '';
    const sourcePart = describeValueSource(f.source, '');
    return `${f.definition || f.label}（${maxPart}重複なし・${sourcePart}・該当無しは []）`;
}
function buildCodeListStringSpec(f) {
    const sep = f.separator || ',';
    const maxPart = f.maxItems ? `最大${f.maxItems}件・` : '';
    const sourcePart = describeValueSource(f.source, '');
    return `${f.definition || f.label}（${maxPart}${sourcePart}・「${sep}」区切り文字列）`;
}
/** 1 フィールドをスキーマ JSON 行として描画。innerIndent は object-array 内部用。 */
function renderFieldLines(field, indent, innerIndent) {
    const lines = [];
    switch (field.kind) {
        case 'text':
            lines.push(`${indent}"${field.key}": "${buildTextSpec(field)}",`);
            break;
        case 'enum-array':
            lines.push(`${indent}"${field.key}": ["${buildEnumArraySpec(field)}"],`);
            if (field.reasonField) {
                lines.push(`${indent}"${field.reasonField.key}": "${buildTextSpec(field.reasonField)}",`);
            }
            break;
        case 'code-list-string':
            lines.push(`${indent}"${field.key}": "${buildCodeListStringSpec(field)}",`);
            if (field.reasonField) {
                lines.push(`${indent}"${field.reasonField.key}": "${buildTextSpec(field.reasonField)}",`);
            }
            break;
        case 'object-array': {
            const minN = field.minItems ?? 1;
            const maxN = field.maxItems ?? 10;
            lines.push(`${indent}"${field.key}": [  // 最低${minN}件・最大${maxN}件`);
            lines.push(`${indent}  {`);
            const childInner = innerIndent + '  ';
            const itemFields = field.itemFields ?? [];
            itemFields.forEach((child, i) => {
                const childLines = renderFieldLines(child, innerIndent, childInner);
                // 末尾要素のカンマ除去
                if (i === itemFields.length - 1 && childLines.length > 0) {
                    const last = childLines[childLines.length - 1];
                    if (last !== undefined)
                        childLines[childLines.length - 1] = last.replace(/,$/, '');
                }
                lines.push(...childLines);
            });
            lines.push(`${indent}  }`);
            lines.push(`${indent}],`);
            break;
        }
    }
    return lines;
}
/**
 * 集約スコアの行 ({aggKey}: {"sum":0,...}) と算出ルールを生成。classifications より前に置く。
 */
function renderTopLevelAggregateScores(fmt, indent) {
    const lines = [];
    const rules = [];
    const agg = fmt.aggregateScores;
    if (!agg)
        return { lines, rules };
    if (agg.positiveKey)
        lines.push(`${indent}"${agg.positiveKey}": {"sum": 0, "max": 0, "avg": 0.0},`);
    if (agg.negativeKey)
        lines.push(`${indent}"${agg.negativeKey}": {"sum": 0, "max": 0, "avg": 0.0},`);
    if (agg.overallKey)
        lines.push(`${indent}"${agg.overallKey}": 0,`);
    if ((agg.positiveKey || agg.negativeKey) && fmt.assessment?.positiveLabel) {
        rules.push('【集約スコアの算出ルール】');
        if (agg.positiveKey)
            rules.push(`- ${agg.positiveKey}: classifications のうち assessment=="${fmt.assessment.positiveLabel}" の score を集約`);
        if (agg.negativeKey)
            rules.push(`- ${agg.negativeKey}: classifications のうち assessment=="${fmt.assessment.negativeLabel}" の score を集約`);
        rules.push('- 各々 {"sum": 合計(整数), "max": 最大(整数), "avg": 平均(小数1位)} のオブジェクト形式。該当なしは {"sum":0,"max":0,"avg":0.0}');
        if (agg.overallKey)
            rules.push(`- ${agg.overallKey}: 総合評価の単一数値（小数1位）`);
    }
    return { lines, rules };
}
/**
 * fields[] 駆動の出力スキーマ記述を生成 (列名動的化レベル 3)。
 * normalizeAiOutputFormat() で fields[] を完全形に整形してから描画する。
 */
function renderOutputSchemaFromFields(fmt) {
    const norm = normalizeAiOutputFormat(fmt);
    const fields = norm.fields ?? [];
    const lines = [];
    lines.push('【出力フォーマット】');
    lines.push('');
    lines.push('必ず以下のJSON形式に厳格に従って出力してください。これ以外のテキストを含めてはいけません。');
    lines.push('**重要**: 厳密な JSON のみを出力すること。コメント (// や /*)、末尾カンマ、マークダウンのコードブロック (```) は出力禁止。');
    lines.push('文字列内のダブルクォートは \\" にエスケープするか全角「」を用いること。文字列内に生の改行を含めないこと（\\n でエスケープ）。');
    lines.push('');
    const hasClassifications = fields.some((f) => f.kind === 'object-array');
    const indent = norm.rootKey ? '    ' : '  ';
    const innerIndent = norm.rootKey ? '      ' : '    ';
    if (hasClassifications) {
        const { rules } = renderTopLevelAggregateScores(norm, indent);
        if (rules.length > 0) {
            lines.push(...rules);
            lines.push('');
        }
    }
    lines.push('{');
    if (norm.rootKey) {
        lines.push(`  "${norm.rootKey}": {`);
    }
    // object-array が存在するなら、その直前にトップレベル集約スコアキーを差し込む
    const topAggLines = hasClassifications ? renderTopLevelAggregateScores(norm, indent).lines : [];
    fields.forEach((field, i) => {
        if (field.kind === 'object-array' && topAggLines.length > 0) {
            lines.push(...topAggLines);
            topAggLines.length = 0;
        }
        const fieldLines = renderFieldLines(field, indent, innerIndent);
        // ルート直下の最終フィールドはカンマ除去
        if (i === fields.length - 1 && fieldLines.length > 0) {
            const last = fieldLines[fieldLines.length - 1];
            if (last !== undefined)
                fieldLines[fieldLines.length - 1] = last.replace(/,$/, '');
        }
        lines.push(...fieldLines);
    });
    if (norm.rootKey) {
        lines.push('  }');
    }
    lines.push('}');
    return lines.join('\n');
}
/** AiOutputFormatMaster から出力スキーマのプロンプト記述を生成。 */
export function formatOutputSchema(fmt) {
    // customSchemaText が設定されている場合は自動生成を上書き (ECW など classifications 構造でないフォーマット用)
    if (fmt.customSchemaText && fmt.customSchemaText.trim().length > 0) {
        return fmt.customSchemaText;
    }
    // fields[] 設定時は列名動的化レベル 3 のデータ駆動描画 (docs/105、opt-in)。
    // 未設定なら以下の legacy renderer をそのまま使い、現行挙動を保持する。
    if (Array.isArray(fmt.fields) && fmt.fields.length > 0) {
        return renderOutputSchemaFromFields(fmt);
    }
    const lines = [];
    lines.push('【出力フォーマット】');
    lines.push('');
    lines.push('必ず以下のJSON形式に厳格に従って出力してください。これ以外のテキストを含めてはいけません。');
    lines.push('classificationsは最低1件・最大10件の配列です。');
    lines.push('**重要**: 厳密な JSON のみを出力すること。コメント (// や /*)、末尾カンマ、マークダウンのコードブロック (```) は出力禁止。');
    lines.push('文字列内のダブルクォートは \\" にエスケープするか全角「」を用いること。文字列内に生の改行を含めないこと（\\n でエスケープ）。');
    lines.push('');
    // 集約スコアの算出ルールを別途プレーンテキストで提示
    const aggRules = [];
    if (fmt.aggregateScores.positiveKey && fmt.aggregateScores.negativeKey) {
        aggRules.push('【集約スコアの算出ルール】');
        aggRules.push(`- ${fmt.aggregateScores.positiveKey}: classificationsのうち assessment=="${fmt.assessment.positiveLabel}" の score を集約`);
        aggRules.push(`- ${fmt.aggregateScores.negativeKey}: classificationsのうち assessment=="${fmt.assessment.negativeLabel}" の score を集約`);
        aggRules.push('- 各々 {"sum": 合計(整数), "max": 最大(整数), "avg": 平均(小数1位)} のオブジェクト形式。該当なしは {"sum":0,"max":0,"avg":0.0}');
        if (fmt.aggregateScores.overallKey) {
            aggRules.push(`- ${fmt.aggregateScores.overallKey}: 総合評価の単一数値（小数1位）`);
        }
    }
    if (aggRules.length > 0) {
        lines.push(...aggRules);
        lines.push('');
    }
    // スキーマ本体 (コメント無し・rootKey 対応)
    const indent = fmt.rootKey ? '    ' : '  ';
    const innerIndent = fmt.rootKey ? '      ' : '    ';
    lines.push('{');
    if (fmt.rootKey) {
        lines.push(`  "${fmt.rootKey}": {`);
    }
    lines.push(`${indent}"description": "画像から読み取った専門的な状況説明（日本語270〜330文字程度・目安）",`);
    lines.push(`${indent}"professional_insight": "専門家の視点による一言での気づき・懸念（日本語30文字程度・目安）",`);
    if (fmt.aggregateScores.positiveKey) {
        lines.push(`${indent}"${fmt.aggregateScores.positiveKey}": {"sum": 0, "max": 0, "avg": 0.0},`);
    }
    if (fmt.aggregateScores.negativeKey) {
        lines.push(`${indent}"${fmt.aggregateScores.negativeKey}": {"sum": 0, "max": 0, "avg": 0.0},`);
    }
    if (fmt.aggregateScores.overallKey) {
        lines.push(`${indent}"${fmt.aggregateScores.overallKey}": 0,`);
    }
    lines.push(`${indent}"reasoning": "画像内の視覚的証拠に基づく推論プロセス（200〜400文字程度・目安）",`);
    lines.push(`${indent}"classifications": [`);
    lines.push(`${indent}  {`);
    lines.push(`${innerIndent}"rank": 1,`);
    for (const ax of fmt.classificationAxes) {
        // 要件8: 軸に definition があればプロンプトのスキーマ記述へ注入 (定義をプロンプト本文へ手書き不要)。
        const axDef = ax.definition?.trim() ? `／${ax.definition.trim()}` : '';
        lines.push(`${innerIndent}"${ax.codeField}": "マスタコード（${ax.label}${axDef}）",`);
        lines.push(`${innerIndent}"${ax.nameField}": "日本語名（${ax.label}）",`);
        if (ax.subField) {
            lines.push(`${innerIndent}"${ax.subField}": ["連鎖コード"],`);
        }
    }
    // 要件8: 評価軸に definition があれば注入。
    const asmDef = fmt.assessment.definition?.trim() ? `（${fmt.assessment.definition.trim()}）` : '';
    lines.push(`${innerIndent}"assessment": "${fmt.assessment.positiveLabel} または ${fmt.assessment.negativeLabel}${asmDef}",`);
    lines.push(`${innerIndent}"assessment_level": "${fmt.assessment.levels.join(' または ')}",`);
    lines.push(`${innerIndent}"score": "符号付き整数（正=${fmt.assessment.positiveLabel}, 負=${fmt.assessment.negativeLabel}, 絶対値: ${fmt.assessment.levels
        .map((l, i) => `${l}=${fmt.assessment.levels.length - i}`)
        .join(', ')}）",`);
    lines.push(`${innerIndent}"${fmt.reasoningField}": "この分類セットの根拠"`);
    lines.push(`${indent}  }`);
    lines.push(`${indent}]`);
    if (fmt.rootKey) {
        lines.push('  }');
    }
    lines.push('}');
    return lines.join('\n');
}
// ============================================================================
// 自己検証チェックリスト (docs/105 §5-2 繰越、old2 promptBuilder
// formatSelfValidationChecklist() / checklistForField() 移植)。
// 出力フォーマットの各 field の制約 (文字数範囲/件数上限/許可値ソース/配列件数) を
// 「出力前に AI が自分で検算するチェック項目」へ展開する。fields[] 未設定フォーマットでも
// normalizeAiOutputFormat() 経由で legacy 派生 fields[] からチェックリストが生成される。
// ============================================================================
/** チェックリスト 1 項目: フィールド種別ごと。 */
function checklistForField(field) {
    const items = [];
    switch (field.kind) {
        case 'text':
            if (field.lengthMin || field.lengthMax) {
                const range = field.lengthMin && field.lengthMax
                    ? `${field.lengthMin}〜${field.lengthMax}文字`
                    : field.lengthMax
                        ? `${field.lengthMax}文字以内`
                        : `${field.lengthMin}文字以上`;
                items.push(`${field.key} は ${range} を目安にしているか（多少の過不足は問題ない）。`);
            }
            break;
        case 'enum-array': {
            const src = describeValueSource(field.source, '');
            const max = field.maxItems ? `最大${field.maxItems}件・` : '';
            items.push(`${field.key} は ${src}・${max}重複なし・または [] か。`);
            if (field.reasonField)
                items.push(...checklistForField(field.reasonField));
            break;
        }
        case 'code-list-string': {
            const src = describeValueSource(field.source, '');
            const max = field.maxItems ? `最大${field.maxItems}件・` : '';
            const sep = field.separator || ',';
            items.push(`${field.key} は ${src}・${max}「${sep}」区切り文字列か。`);
            if (field.reasonField)
                items.push(...checklistForField(field.reasonField));
            break;
        }
        case 'object-array': {
            const min = field.minItems ?? 1;
            const max = field.maxItems ?? 10;
            items.push(`${field.key} は ${min}〜${max} 件の配列か。`);
            for (const child of field.itemFields ?? []) {
                items.push(...checklistForField(child));
            }
            break;
        }
    }
    return items;
}
/**
 * フォーマット定義から自己検証チェックリストを生成 (汎用 fields[] ベース)。
 * - fmt が null/undefined なら空文字。
 * - customSchemaText 設定時は fields[] による細目を生成できない (スキーマが自由記述のため)
 *   ので、基本ルール (JSON 構文/エスケープ/末尾カンマ) のみの簡易チェックリストを返す
 *   (old2 同様の簡易方針)。
 */
export function formatSelfValidationChecklist(fmt) {
    if (!fmt)
        return '';
    const items = [
        '出力は { で始まり } で終わる JSON 1個のみか。',
        '文字列内部の " は \\" にエスケープ、または「」を使用しているか。',
        '末尾カンマ・JS コメント・コードフェンス・余計な文が無いか。',
    ];
    // customSchemaText のときは自由記述スキーマのため field 細目は生成しない (簡易)。
    if (!(fmt.customSchemaText && fmt.customSchemaText.trim().length > 0)) {
        const norm = normalizeAiOutputFormat(fmt);
        for (const f of norm.fields ?? [])
            items.push(...checklistForField(f));
    }
    const lines = ['【自己検証チェックリスト（出力前に必ず満たす）】'];
    items.forEach((it, i) => lines.push(`${i + 1}. ${it}`));
    return lines.join('\n');
}
function withHeading(heading, body) {
    if (!body || !body.trim())
        return null;
    return `【${heading}】\n${body.trim()}`;
}
/**
 * promptSections を固定順でアセンブル。地区側 overrides があれば各セクションを差し替える
 * (rules は上書き不可: マスタ管理範囲)。各セクションは見出し付きでブロック化。
 */
function assembleSystemPromptFromSections(sections, overrides, ctx) {
    const role = overrides?.role ?? sections.role;
    const task = overrides?.task ?? sections.task;
    const contentRules = overrides?.contentRules ?? sections.contentRules;
    const classificationRules = overrides?.classificationRules ?? sections.classificationRules;
    const cautions = overrides?.cautions ?? sections.cautions;
    const outputSchemaNotes = overrides?.outputSchemaNotes ?? sections.outputSchemaNotes;
    const extraNotes = overrides?.extraNotes ?? sections.extraNotes;
    const extraRules = sections.rules; // 地区側上書き不可
    const parts = [];
    if (role && role.trim())
        parts.push(role.trim());
    if (task && task.trim())
        parts.push(task.trim());
    // 入力データブロック
    if (ctx.postFieldsBlock && ctx.postFieldsBlock.trim())
        parts.push(ctx.postFieldsBlock.trim());
    // 参照マスタ群
    for (const mb of ctx.masterBlocks) {
        if (!mb.body || !mb.body.trim())
            continue;
        parts.push(`【参照マスタ: ${mb.displayName}】\n${mb.body.trim()}`);
    }
    parts.push(withHeading('内容ルール', contentRules));
    parts.push(withHeading('分類ルール', classificationRules));
    parts.push(withHeading('注意点', cautions));
    // 厳格出力ルール (常時) + 追加 rules
    const ruleParts = [DEFAULT_STRICT_RULES];
    if (extraRules && extraRules.trim())
        ruleParts.push(extraRules.trim());
    parts.push(ruleParts.join('\n\n'));
    // 出力スキーマ (outputFormat 設定済みの場合のみ)
    if (ctx.hasOutputFormat && ctx.outputSchemaBlock && ctx.outputSchemaBlock.trim()) {
        parts.push(ctx.outputSchemaBlock.trim());
    }
    parts.push(withHeading('出力スキーマに対する追加指示', outputSchemaNotes));
    // 自動チェックリスト
    if (ctx.checklistBlock && ctx.checklistBlock.trim())
        parts.push(ctx.checklistBlock.trim());
    if (extraNotes && extraNotes.trim())
        parts.push(extraNotes.trim());
    return parts.filter((p) => !!p).join('\n\n');
}
/** {{var}} を values で展開 (assembled テキスト用)。 */
function expandTemplates(text, values) {
    return text.replace(/\{\{(\w+)\}\}/g, (_, key) => values[key] ?? '');
}
/**
 * 1 つの promptInputMapping が表す postField key 候補を列挙する (inputFieldKeys フィルタ用)。
 * templateVar 本体 + source の各候補 (`|` 区切り) の末尾セグメント (`extraFields.health` → `health`) を見る。
 * 統合エディタ (P4) は postFields から mapping を自動生成するため key が一致する前提だが、
 * 旧来の手書き mapping でも source 末尾 / templateVar のどちらかが一致すれば通す寛容な照合。
 */
function mappingPostFieldKeys(m) {
    const keys = [m.templateVar];
    for (const cand of m.source
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean)) {
        const seg = cand.split('.').pop();
        if (seg)
            keys.push(seg);
    }
    return keys;
}
/**
 * 1 つの mapping に対応する postField を地区の投稿設定から引く。
 * source (`post.extraFields.HappinessScore` 等) の末尾セグメントを優先し (`|` フォールバック
 * 指定は先に書かれた候補を優先)、見つからなければ templateVar 名で引く。
 * `mappingPostFieldKeys` と同じ候補集合を、優先順位付きで辿るだけの関係。
 */
function findPostFieldForMapping(m, byKey) {
    for (const key of mappingPostFieldKeys(m).slice(1)) {
        const f = byKey.get(key);
        if (f)
            return f;
    }
    return byKey.get(m.templateVar);
}
/** 投稿設定から自動で AI に送る対象にしない型 (写真・位置は本文と別経路で送る / 送らない)。 */
const AUTO_MAPPING_EXCLUDED_TYPES = new Set(['photo', 'location']);
/**
 * 入力の対応表 (promptInputMapping) に、地区の投稿設定の項目を自動で足す (2026-10-08)。
 *
 * 対応表に載っていない入力項目 (地区独自の項目、テンプレに無い項目) も AI に送るため、
 * 非表示・写真・位置以外で、既存の対応表のどの行にも当たらない項目を
 * `{ templateVar: <内部キー>, source: post.extraFields.<内部キー> }` として末尾に足す
 * (タイトル / コメントは post 直下)。どの項目を実際に送るかは従来どおり inputFieldKeys で絞る
 * (未設定 = 全部)。値が無い項目は【投稿データ】に出ない (空文字は従来どおり除外)。
 * 既存の行は変えないので、テンプレの {{変数}} や既存地区のプロンプトの並びは保たれる。
 */
export function withPostFieldMappings(mapping, postFields) {
    const base = mapping ?? [];
    if (!postFields || postFields.length === 0)
        return base;
    const covered = new Set(base.flatMap((m) => mappingPostFieldKeys(m)));
    const added = [];
    for (const f of postFields) {
        if (!f || typeof f.key !== 'string' || !f.key)
            continue;
        if (f.mode === 'hidden' || AUTO_MAPPING_EXCLUDED_TYPES.has(f.type))
            continue;
        if (covered.has(f.key))
            continue;
        covered.add(f.key);
        const source = f.key === 'title' || f.key === 'comment' ? `post.${f.key}` : `post.extraFields.${f.key}`;
        added.push({ templateVar: f.key, source });
    }
    return added.length > 0 ? [...base, ...added] : base;
}
/** 負の値を含むスケールでは正の値に + を付けて「-3〜+3」の見た目にする (old2 踏襲)。 */
function formatScalePoint(n, min) {
    return min < 0 && n > 0 ? `+${n}` : String(n);
}
/**
 * `optionsMeta` の min / max / excludeZero / scaleLabels を人が読める 1 行にまとめる。
 * 例: `スケール: -3〜+3 の6段階（0 なし）、-3:孤立 〜 +3:つながり`
 * 段階スケールで min / max 未設定なら投稿フォームと同じ既定 (-3〜+3) を使う。
 */
function formatScaleInfo(field) {
    const meta = field.optionsMeta;
    if (field.type === 'rating-scale') {
        const min = typeof meta?.min === 'number' ? meta.min : -3;
        const max = typeof meta?.max === 'number' ? meta.max : 3;
        if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min)
            return null;
        const excludeZero = meta?.excludeZero === true && min <= 0 && max >= 0;
        const steps = max - min + 1 - (excludeZero ? 1 : 0);
        const range = `${formatScalePoint(min, min)}〜${formatScalePoint(max, min)}`;
        const parts = [`${range} の${steps}段階${excludeZero ? '（0 なし）' : ''}`];
        const neg = meta?.scaleLabels?.negLabel?.trim();
        const pos = meta?.scaleLabels?.posLabel?.trim();
        if (neg || pos) {
            const lo = `${formatScalePoint(min, min)}:${neg || '低い'}`;
            const hi = `${formatScalePoint(max, min)}:${pos || '高い'}`;
            parts.push(`${lo} 〜 ${hi}`);
        }
        return `スケール: ${parts.join('、')}`;
    }
    const hasMin = typeof meta?.min === 'number';
    const hasMax = typeof meta?.max === 'number';
    if (!hasMin && !hasMax)
        return null;
    return `範囲: ${hasMin ? meta?.min : ''}〜${hasMax ? meta?.max : ''}`;
}
/** プロンプトに列挙する選択肢の上限 (超えたぶんは件数だけ示して打ち切る)。 */
const MAX_PROMPT_OPTIONS = 30;
/** 単一選択 / 複数選択の選択肢を 1 行にする。保存値と表示名が違うときは両方示す。 */
function formatOptionsInfo(field) {
    const options = normalizeFieldOptions(field.options);
    if (options.length === 0)
        return null;
    const shown = options
        .slice(0, MAX_PROMPT_OPTIONS)
        .map((o) => (o.label && o.label !== o.value ? `${o.label}（保存値: ${o.value}）` : o.value));
    const rest = options.length - shown.length;
    return `選択肢: ${shown.join(' / ')}${rest > 0 ? ` ほか${rest}件` : ''}`;
}
/** 複数行の値が次の項目 (`- ` 始まり) と紛れないよう、2 行目以降を字下げする。 */
function indentValue(value) {
    return value.split('\n').join('\n    ');
}
/**
 * 1 項目を投稿設定 (postField) 駆動の詳細形式で描画する (old2 の投稿データブロック踏襲)。
 *
 *     - Happy / Unhappy（key: happiness, 型: 段階スケール, 投稿設定キー: HappinessScore） ［補足: …］
 *       値: 3
 *       定義: 対象に対する幸福感の主観評価（-3:Unhappy〜+3:Happy）。…
 *       スケール: -3〜+3 の6段階（0 なし）、-3:Unhappy 〜 +3:Happy
 *
 * `key:` に出すのは **templateVar** (= 実際に AI へ渡している値の名前)。投稿設定側の key が
 * 異なる場合は「投稿設定キー」として併記する — マスタの使用ルール等が投稿設定の key
 * (例 `HappinessScore`) で書かれていても、AI が値と結び付けられるようにするため。
 */
function renderPostFieldDetail(m, field, value, extraFields) {
    const head = [`key: ${m.templateVar}`, `型: ${FIELD_TYPE_LABELS[field.type] ?? field.type}`];
    if (field.key && field.key !== m.templateVar)
        head.push(`投稿設定キー: ${field.key}`);
    const label = field.label?.trim() || m.templateVar;
    const description = field.description?.trim();
    const lines = [
        `- ${label}（${head.join(', ')}）${description ? ` ［補足: ${description}］` : ''}`,
        `  値: ${indentValue(value)}`,
    ];
    // 選択肢「その他」の記入内容 (`{key}__other`、2026-10-06)。記入があるときだけ足す
    // (記入の無い投稿のプロンプトは変わらない)。
    const other = findOtherOption({
        ...field,
        options: normalizeFieldOptions(field.options),
    });
    if (other && extraFields && isOtherChosen(other.value, extraFields[field.key])) {
        const text = readOtherText(extraFields, field.key);
        if (text) {
            const safe = truncateForPrompt(sanitizeForPrompt(text), PROMPT_POST_VALUE_LIMITS.extraField);
            lines.push(`  ${other.label}の内容: ${indentValue(safe)}`);
        }
    }
    const definition = field.definition?.trim();
    if (definition)
        lines.push(`  定義: ${definition}`);
    const scale = formatScaleInfo(field);
    if (scale)
        lines.push(`  ${scale}`);
    const options = formatOptionsInfo(field);
    if (options)
        lines.push(`  ${options}`);
    return lines.join('\n');
}
/**
 * 投稿データブロックを promptInputMapping の値から生成 (promptSections モード用)。
 * 自動生成系の var (categoryMaster 群 / outputSchema / checklist) は除外する。
 *
 * 形式は 2 通り (2026-08-31、old2 の投稿設定駆動な詳細形式へ復帰):
 *   - `postFields` (地区の `dataSettings.postFields`) が渡されたら **詳細形式**。
 *     ラベル・型・補足説明・定義・スケール・選択肢を投稿設定から出すため、入力値の意味を
 *     プロンプト本文へ手書きする二重管理が要らなくなる (投稿設定を変えれば追従する)。
 *   - 渡されない / 対応する postField が見つからない項目は従来の `- {templateVar}: {value}`。
 *     dataSettings を持たない地区でも情報が欠落しないようにするフォールバック。
 * どちらの形式でも「AI に渡る値の名前 (templateVar)」と値の対応は変えない。
 *
 * 要件5 (old2 `inputFieldKeys` 踏襲): `inputFieldKeys` が渡されたら、対応 postField key が
 * その集合に含まれる mapping のみ送る。
 *   - undefined: フィルタ無し (全 mapping、現行挙動・回帰ゼロ)
 *   - []: 何も送らない (空ブロック)
 *   - 明示配列: 一致する mapping のみ
 */
/** post.extraFields を取り出す (無い / 形が違えば undefined)。 */
function postExtraFields(post) {
    const ef = post.extraFields;
    return ef && typeof ef === 'object' && !Array.isArray(ef)
        ? ef
        : undefined;
}
function buildPostFieldsBlock(mapping, values, inputFieldKeys, postFields, extraFields) {
    if (!mapping || mapping.length === 0)
        return '';
    const allow = inputFieldKeys === undefined ? null : new Set(inputFieldKeys);
    const byKey = new Map();
    for (const f of postFields ?? []) {
        if (f && typeof f.key === 'string' && f.key && !byKey.has(f.key))
            byKey.set(f.key, f);
    }
    const entries = mapping
        .map((m) => {
        if (allow && !mappingPostFieldKeys(m).some((k) => allow.has(k)))
            return null;
        const v = values[m.templateVar];
        if (v === undefined || v === '')
            return null;
        const field = byKey.size > 0 ? findPostFieldForMapping(m, byKey) : undefined;
        return field ? renderPostFieldDetail(m, field, v, extraFields) : `- ${m.templateVar}: ${v}`;
    })
        .filter((l) => l !== null);
    if (entries.length === 0)
        return '';
    return ['【投稿データ】', ...entries].join('\n');
}
export function buildPrompt({ settings, post, categoryMaster, categoryMasters, outputFormat, promptSectionOverrides, inputFieldKeys, postFields, extras, }) {
    // 対応表 + 地区の投稿設定の項目 (対応表に無いもの) を自動で足した実効の入力対応表。
    const inputMapping = withPostFieldMappings(settings.promptInputMapping, postFields);
    const values = {};
    for (const m of inputMapping) {
        const raw = resolveFromMapping(post, m.source);
        if (m.templateVar === 'happiness' || m.templateVar === 'health') {
            const n = normalizeScore(raw);
            values[m.templateVar] = n === null ? 'null' : String(n);
        }
        else {
            values[m.templateVar] = sanitizePostValue(raw ?? '不明', m.source);
        }
    }
    // 複数マスタ展開: 各マスタの templateVar に対応するプレースホルダーを展開 +
    // promptSections アセンブラ用の masterBlocks を収集 (old2 buildPrompt 踏襲)。
    const masterBlocks = [];
    if (categoryMasters && categoryMasters.length > 0) {
        for (const master of categoryMasters) {
            const varName = master.templateVar || 'categoryMaster';
            const body = formatCategoryMaster(master);
            values[varName] = body;
            // usageRules も展開 ({{varName_rules}} で参照可能)
            if (master.usageRules) {
                values[`${varName}_rules`] = master.usageRules;
            }
            masterBlocks.push({
                templateVar: varName,
                displayName: master.displayName || master.id || varName,
                body,
            });
        }
    }
    else if (categoryMaster) {
        // 後方互換: 単一マスタ (categoryMasters が未設定の場合)
        const body = formatCategoryMaster(categoryMaster);
        values.categoryMaster = body;
        masterBlocks.push({
            templateVar: 'categoryMaster',
            displayName: categoryMaster.displayName || categoryMaster.id || 'categoryMaster',
            body,
        });
    }
    // 後方互換 (legacy systemInstructions モード): {{categoryMaster}} 未設定なら「（マスタ未設定）」を入れる。
    if (!values.categoryMaster) {
        values.categoryMaster = formatCategoryMaster(categoryMaster);
    }
    // 出力フォーマットマスタからスキーマ記述・自己検証チェックリストを展開
    let selfValidationChecklist = '';
    if (outputFormat) {
        values.outputSchema = formatOutputSchema(outputFormat);
        selfValidationChecklist = formatSelfValidationChecklist(outputFormat);
        values.selfValidationChecklist = selfValidationChecklist;
    }
    if (extras) {
        for (const [k, v] of Object.entries(extras)) {
            values[k] = sanitizeForPrompt(v);
        }
    }
    // docs/105 Phase 3: promptSections があればセクション自動アセンブル。
    // なければ従来の systemInstructions verbatim 展開 (後方互換・回帰ゼロ)。
    if (settings.promptSections) {
        const ctx = {
            postFieldsBlock: buildPostFieldsBlock(inputMapping, values, inputFieldKeys, postFields, postExtraFields(post)),
            masterBlocks,
            outputSchemaBlock: values.outputSchema ?? '',
            checklistBlock: values.selfValidationChecklist ?? '',
            hasOutputFormat: !!outputFormat,
        };
        const assembled = assembleSystemPromptFromSections(settings.promptSections, promptSectionOverrides, ctx);
        return expandTemplates(assembled, values);
    }
    const hasChecklistPlaceholder = /\{\{selfValidationChecklist\}\}/.test(settings.systemInstructions);
    let prompt = settings.systemInstructions.replace(/\{\{(\w+)\}\}/g, (_, key) => {
        return values[key] ?? '';
    });
    // プレースホルダが無い場合、outputFormat があるときのみチェックリストブロックを末尾に追記。
    // (プレースホルダがある場合は上の置換機構で既に差し込み済み。outputFormat が無いときは
    //  現行挙動を保持して何も追加しない。)
    if (!hasChecklistPlaceholder && selfValidationChecklist) {
        prompt = `${prompt}\n\n${selfValidationChecklist}`;
    }
    return prompt;
}
/**
 * titleGeneration 用プロンプトを組み立てる。instructions 未設定なら空文字を返す
 * (呼出側はそれを見て title 生成を skip する)。
 */
export function buildTitlePrompt({ settings, post, analysis, inputFieldKeys, postFields, }) {
    const instructions = settings.titleGeneration?.instructions?.trim();
    if (!instructions)
        return '';
    // 入力値算出 (buildPrompt と同一ロジック、対応表に無い投稿設定の項目も自動で足す)。
    const inputMapping = withPostFieldMappings(settings.promptInputMapping, postFields);
    const values = {};
    for (const m of inputMapping) {
        const raw = resolveFromMapping(post, m.source);
        if (m.templateVar === 'happiness' || m.templateVar === 'health') {
            const n = normalizeScore(raw);
            values[m.templateVar] = n === null ? 'null' : String(n);
        }
        else {
            values[m.templateVar] = sanitizePostValue(raw ?? '不明', m.source);
        }
    }
    // 解析結果を values (placeholder 互換) + 参考ブロックへ。
    const analysisLines = [];
    if (analysis && typeof analysis === 'object') {
        const a = analysis;
        const desc = typeof a.description === 'string' ? a.description : '';
        const insight = typeof a.professional_insight === 'string' ? a.professional_insight : '';
        if (desc) {
            values.description = sanitizeForPrompt(desc);
            analysisLines.push(`- description: ${desc}`);
        }
        if (insight) {
            values.professional_insight = sanitizeForPrompt(insight);
            analysisLines.push(`- professional_insight: ${insight}`);
        }
        const cls = Array.isArray(a.classifications) ? a.classifications : [];
        if (cls.length > 0) {
            const items = cls
                .slice(0, 10)
                .map((c) => {
                const o = (c ?? {});
                const name = [o.aspect_name, o.category_name, o.gql_name].find((x) => typeof x === 'string' && x.length > 0);
                const asmt = typeof o.assessment === 'string' ? o.assessment : '';
                return name ? `${name}${asmt ? `(${asmt})` : ''}` : '';
            })
                .filter(Boolean);
            if (items.length > 0)
                analysisLines.push(`- 所見: ${items.join(' / ')}`);
        }
    }
    const postFieldsBlock = buildPostFieldsBlock(inputMapping, values, inputFieldKeys, postFields, postExtraFields(post));
    const parts = [];
    // instructions ({{}} があれば展開、無くても可)。
    parts.push(expandTemplates(instructions, values));
    if (postFieldsBlock && postFieldsBlock.trim())
        parts.push(postFieldsBlock.trim());
    if (analysisLines.length > 0) {
        parts.push(['【AI解析結果（参考）】', ...analysisLines].join('\n'));
    }
    return parts.filter((p) => p && p.trim()).join('\n\n');
}
// for test reuse
export const _promptInternals = { resolveFromMapping };
//# sourceMappingURL=promptBuilder.js.map